import { ReactElement } from "react";
import memoizeOne from "memoize-one";
import type {
    AddPhoneNumberOptions,
    AudioDeviceInfo,
    BackgroundBlurConfig,
    BackgroundReplacementConfig,
    CallSurvey,
    CallSurveyResponse,
    ParticipantCapabilities,
    PermissionConstraints,
    Reaction,
    StartCallOptions,
    VideoDeviceInfo,
} from "@azure/communication-calling";
import { DtmfTone } from "@azure/communication-calling";
import {
    CallAdapterState,
    CommonCallAdapter,
    CommonCallingHandlers,
    CommonProperties,
    fromFlatCommunicationIdentifier,
    StartCaptionsAdapterOptions,
    toFlatCommunicationIdentifier,
    VideoBackgroundBlurEffect,
    VideoBackgroundReplacementEffect,
    VideoStreamOptions,
} from "@azure/communication-react";
import { useAdapter } from "../provider/CallAdapterProvider";
import { isCameraOn } from "../utils/Utils";
import { CommunicationIdentifier, CommunicationUserIdentifier, PhoneNumberIdentifier } from "@azure/communication-common";
import { useCallAdapterSelector } from "./useSelector";


const _toCommunicationIdentifier = (id: string | CommunicationIdentifier): CommunicationIdentifier => {
    if (typeof id === 'string') {
        return fromFlatCommunicationIdentifier(id);
    }
    return id;
};


// NOTE: custom extensions as  at the time of writing,
//       not all handlers are available in the public API
interface CustomCommonCallingHandlers extends CommonCallingHandlers {
    askDevicePermission: (constrain: PermissionConstraints) => Promise<void>;
}

type AdapterCommonCallingHandlers = Omit<CommonCallingHandlers, 'onAcceptCall' | 'onRejectCall'>;


const getCapabilites = (state: CallAdapterState): ParticipantCapabilities | undefined =>
  state.call?.capabilitiesFeature?.capabilities;

export const useHandlers = <PropsT>(

  _component: (props: PropsT) => ReactElement | null
): Pick<AdapterCommonCallingHandlers, CommonProperties<AdapterCommonCallingHandlers, PropsT>> => {
  const adapter = useAdapter();
  const capabilities = useCallAdapterSelector(getCapabilites);
  return createCompositeHandlers(adapter, capabilities);
};


const createCompositeHandlers = memoizeOne(
    (
        adapter: CommonCallAdapter,
        capabilities?: ParticipantCapabilities
    ): AdapterCommonCallingHandlers => ({
        onCreateLocalStreamView: async (options) => await adapter.createStreamView(undefined, options),
        onCreateRemoteStreamView: async (userId, options) => await adapter.createStreamView(userId, options),
        onHangUp: async (forEveryone?: boolean) => {
            await adapter.leaveCall(forEveryone);
        },
        onToggleHold: async () => adapter.getState().call?.state === 'LocalHold' ? await adapter.resumeCall() : await adapter.holdCall(),
        onAddParticipant: async (
            participant: Partial<CommunicationUserIdentifier & PhoneNumberIdentifier>,
            options?: AddPhoneNumberOptions
        ) => {
            if ('communicationUserId' in participant) {
            return await adapter.addParticipant(participant as CommunicationUserIdentifier);
            } else if ('phoneNumber' in participant) {
            return await adapter.addParticipant(participant as PhoneNumberIdentifier, options);
            }
        },
        onSendDtmfTone: async (dtmfTone: DtmfTone) => {
            await adapter.sendDtmfTone(dtmfTone);
        },
        onRemoveParticipant: async (userId: string | CommunicationIdentifier) => {
            if (typeof userId === 'string') {
            await adapter.removeParticipant(userId);
            } else {
            await adapter.removeParticipant(_toCommunicationIdentifier(userId));
            }
        },
        onRaiseHand: async () => {
            await adapter.raiseHand();
        },
        onLowerHand: async () => {
            await adapter.lowerHand();
        },
        onToggleRaiseHand: async () => {
            adapter.getState().call?.raiseHand.localParticipantRaisedHand
            ? await adapter.lowerHand()
            : await adapter.raiseHand();
        },
        onReactionClick: async (reaction: Reaction) => {
            await adapter.onReactionClick(reaction);
        },
        onSelectCamera: async (deviceInfo: VideoDeviceInfo, options: VideoStreamOptions | undefined) => {
            await adapter.setCamera(deviceInfo, options);
        },
        onSelectMicrophone: async (deviceInfo: AudioDeviceInfo) => {
            await adapter.setMicrophone(deviceInfo);
        },
        onSelectSpeaker: async (deviceInfo: AudioDeviceInfo) => {
            await adapter.setSpeaker(deviceInfo);
        },
        onStartCall: (participants: CommunicationIdentifier[], options?: StartCallOptions) => {
            const rawIds = participants.map((participant) => toFlatCommunicationIdentifier(participant));
            return adapter.startCall(rawIds, options);
        },
        onStartScreenShare: async () => {
            await adapter.startScreenShare();
        },
        onStopScreenShare: async () => {
            await adapter.stopScreenShare();
        },
        onToggleCamera: async (options: VideoStreamOptions | undefined) => {
            isCameraOn(adapter.getState()) ? await adapter.stopCamera() : await adapter.startCamera(options);
        },
        onToggleMicrophone: async () => adapter.getState().call?.isMuted ? await adapter.unmute() : await adapter.mute(),
        onToggleScreenShare: async () => adapter.getState().call?.isScreenSharingOn
            ? await adapter.stopScreenShare()
            : await adapter.startScreenShare(),
        onStartLocalVideo: async () => {
            if (adapter.getState().call) {
            return adapter.startCamera();
            }
        },
        onDisposeLocalStreamView: async () => adapter.disposeLocalVideoStreamView(),
        onDisposeRemoteStreamView: async (userId: string) => adapter.disposeStreamView(userId),
        onDisposeRemoteScreenShareStreamView: async (userId: string) => adapter.disposeScreenShareStreamView(userId),
        onDisposeLocalScreenShareStreamView: async () => adapter.disposeScreenShareStreamView(''),
        onDisposeRemoteVideoStreamView: async (userId: string) => adapter.disposeRemoteVideoStreamView(userId),
        /* @conditional-compile-remove(call-readiness) */
        askDevicePermission: async (constrain: PermissionConstraints) => {
            await adapter.askDevicePermission(constrain);
        },

        onRemoveVideoBackgroundEffects: async () => await adapter.stopVideoBackgroundEffects(),

        onBlurVideoBackground: async (backgroundBlurConfig?: BackgroundBlurConfig) => {
            const blurConfig: VideoBackgroundBlurEffect = {
            effectName: 'blur',
            ...backgroundBlurConfig
            };
            return await adapter.startVideoBackgroundEffect(blurConfig);
        },

        onReplaceVideoBackground: async (backgroundReplacementConfig: BackgroundReplacementConfig) => {
            const replacementConfig: VideoBackgroundReplacementEffect = {
            effectName: 'replacement',
            ...backgroundReplacementConfig
            };
            return await adapter.startVideoBackgroundEffect(replacementConfig);
        },

        onStartNoiseSuppressionEffect: async () => await adapter.startNoiseSuppressionEffect(),

        onStopNoiseSuppressionEffect: async () => await adapter.stopNoiseSuppressionEffect(),

        onStartCaptions: async (options: StartCaptionsAdapterOptions | undefined) => {
            await adapter.startCaptions(options);
        },
        onStopCaptions: async () => {
            await adapter.stopCaptions();
        },
        onSetSpokenLanguage: async (language: string) => {
            await adapter.setSpokenLanguage(language);
        },
        onSetCaptionLanguage: async (language: string) => {
            await adapter.setCaptionLanguage(language);
        },
        onSubmitSurvey: async (survey: CallSurvey): Promise<CallSurveyResponse | undefined> => await adapter.submitSurvey(survey),
        onStartSpotlight: async (userIds?: string[]): Promise<void> => {
            await adapter.startSpotlight(userIds);
        },
        onStopSpotlight: async (userIds?: string[]): Promise<void> => {
            await adapter.stopSpotlight(userIds);
        },
        onStopAllSpotlight: async (): Promise<void> => {
            await adapter.stopAllSpotlight();
        },
        onMuteParticipant: async (userId: string): Promise<void> => {
            await adapter.muteParticipant(userId);
        },
        onMuteAllRemoteParticipants: async (): Promise<void> => {
            await adapter.muteAllRemoteParticipants();
        },
        onForbidAudio: capabilities?.forbidOthersAudio?.isPresent
            ? async (userIds: string[]): Promise<void> => {
                await adapter.forbidAudio(userIds);
            }
            : undefined,
        onPermitAudio: capabilities?.forbidOthersAudio?.isPresent
            ? async (userIds: string[]): Promise<void> => {
                await adapter.permitAudio(userIds);
            }
            : undefined,
        onForbidOthersAudio: capabilities?.forbidOthersAudio?.isPresent
            ? async (): Promise<void> => {
                await adapter.forbidOthersAudio();
            }
            : undefined,
        onPermitOthersAudio: capabilities?.forbidOthersAudio?.isPresent
            ? async (): Promise<void> => {
                await adapter.permitOthersAudio();
            }
            : undefined,
        onForbidVideo: capabilities?.forbidOthersVideo?.isPresent
            ? async (userIds: string[]): Promise<void> => {
                await adapter.forbidVideo(userIds);
            }
            : undefined,
        onPermitVideo: capabilities?.forbidOthersVideo?.isPresent
            ? async (userIds: string[]): Promise<void> => {
                await adapter.permitVideo(userIds);
            }
            : undefined,
        onForbidOthersVideo: capabilities?.forbidOthersVideo?.isPresent
            ? async (): Promise<void> => {
                await adapter.forbidOthersVideo();
            }
            : undefined,
        onPermitOthersVideo: capabilities?.forbidOthersVideo?.isPresent
            ? async (): Promise<void> => {
                await adapter.permitOthersVideo();
            }
            : undefined,
        onCreateTogetherModeStreamView: async (options) => await adapter.createTogetherModeStreamView(options),
        onStartTogetherMode: async () => await adapter.startTogetherMode(),
        onSetTogetherModeSceneSize: (width: number, height: number) => adapter.setTogetherModeSceneSize(width, height),
        onDisposeTogetherModeStreamView: async () => await adapter.disposeTogetherModeStreamView(),

        onSendRealTimeText: async (text: string, isFinalized: boolean) => await adapter.sendRealTimeText(text, isFinalized)
    })
);
